import React, { useState, useMemo, useEffect } from 'react';
import { useChat } from '../../context/ChatContext';
import SearchBar from './Sidebar/SearchBar';
import ConversationItem from './Sidebar/ConversationItem';
import NewMessageModal from './Sidebar/NewMessageModal';
import { ConversationListSkeleton } from './TalkSkeleton';



const ChatSidebar = () => {
    const {
        conversations,
        activeConversationId,
        setActiveConversationId,
        fetchConversations,
        loadMoreConversations,
        hasMore,
        isLoadingMore
    } = useChat();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [isNewMessageModalOpen, setIsNewMessageModalOpen] = useState(false);

    // Get current user from localStorage
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setCurrentUser(user);
    }, []);

    // Filter conversations based on search query
    const filteredConversations = useMemo(() => {
        if (!searchQuery.trim()) {
            return conversations;
        }

        const query = searchQuery.toLowerCase();
        return conversations.filter(conversation => {
            // Search by conversation name
            if (conversation.name?.toLowerCase().includes(query)) {
                return true;
            }

            // Search by team context name
            if (conversation.context?.name?.toLowerCase().includes(query)) {
                return true;
            }

            // Search by participant names
            const participantNames = conversation.users?.map(u => u.name?.toLowerCase() || '') || [];
            if (participantNames.some(name => name.includes(query))) {
                return true;
            }

            // Search by last message
            if (conversation.last_message?.body?.toLowerCase().includes(query)) {
                return true;
            }

            return false;
        });
    }, [conversations, searchQuery]);

    const handleNewMessage = () => {
        setIsNewMessageModalOpen(true);
    };

    const handleConversationCreated = async (conversation) => {
        // Refresh conversations list to include the new conversation
        await fetchConversations(1);
        // Set the newly created conversation as active
        setActiveConversationId(conversation.id);
        setIsNewMessageModalOpen(false);
    };

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        // Check if scrolled to bottom with a small threshold (e.g., 20px)
        if (scrollHeight - scrollTop <= clientHeight + 20) {
            if (hasMore && !isLoadingMore && !searchQuery) {
                loadMoreConversations();
            }
        }
    };

    const isLoading = !Array.isArray(conversations) && !conversations.length; // Modified check
    const hasConversations = Array.isArray(conversations) && conversations.length > 0;
    const hasFilteredResults = filteredConversations.length > 0;

    return (
        <div className="talk-sidebar">
            <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search"
                onNewMessage={handleNewMessage}
            />

            <div className="talk-conversation-list" onScroll={handleScroll}>
                {isLoading && !isLoadingMore ? (
                    <ConversationListSkeleton />
                ) : !hasConversations && !isLoading ? (
                    <div className="talk-empty-state">
                        <div className="talk-empty-icon">💬</div>
                        <h4>No conversations yet</h4>
                        <p>Click + to start a new chat</p>
                    </div>
                ) : !hasFilteredResults && searchQuery ? (
                    <div className="talk-empty-state">
                        <div className="talk-empty-icon">🔍</div>
                        <h4>No results found</h4>
                        <p>Try searching for a different name</p>
                    </div>
                ) : (
                    <>
                        {filteredConversations.map(conversation => (
                            <ConversationItem
                                key={conversation.id}
                                conversation={conversation}
                                isActive={String(conversation.id) === String(activeConversationId)}
                                onClick={() => setActiveConversationId(conversation.id)}
                                currentUser={currentUser}
                            />
                        ))}
                        {isLoadingMore && (
                            <div className="talk-conversation-loading-more">
                                Loading more...
                            </div>
                        )}
                    </>
                )}
            </div>

            <NewMessageModal
                isOpen={isNewMessageModalOpen}
                onClose={() => setIsNewMessageModalOpen(false)}
                onConversationCreated={handleConversationCreated}
            />
        </div>
    );
};

export default ChatSidebar;
